import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MAIN_NAV, MORE_GROUPS } from "@/components/site/nav";

const linkCls = ({ isActive }) =>
  `font-display uppercase text-lg tracking-wide transition-colors duration-200 ${
    isActive ? "text-primary" : "text-white hover:text-primary"
  }`;

const MORE_PATHS = MORE_GROUPS.flatMap((g) => g.links.map((l) => l.to));

const MoreMenu = () => {
  const { pathname } = useLocation();
  const active = MORE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        data-testid="nav-more-dropdown"
        className={`font-display uppercase text-lg tracking-wide transition-colors duration-200 flex items-center gap-1 outline-none ${active ? "text-primary" : "text-white hover:text-primary"}`}
      >
        Plus <ChevronDown className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-none bg-[hsl(var(--popover))] border-white/10 min-w-[240px] p-2" data-testid="nav-more-menu">
        {MORE_GROUPS.map((g, i) => (
          <div key={g.key}>
            {i > 0 && <DropdownMenuSeparator className="bg-white/10" />}
            <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.25em] text-primary font-bold">{g.title}</DropdownMenuLabel>
            {g.links.map((l) => (
              <DropdownMenuItem key={l.to} asChild className="rounded-none focus:bg-primary focus:text-white">
                <Link to={l.to} data-testid={`nav-more-${l.key}`} className="font-display uppercase text-base py-2.5 cursor-pointer">
                  {l.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileMenu = ({ onClose }) => (
  <nav className="lg:hidden bg-black border-t border-white/10 px-5 py-8 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
    <div className="flex flex-col gap-3">
      {MAIN_NAV.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.key}`}
          className={({ isActive }) => `font-display uppercase text-4xl leading-none ${isActive ? "text-primary" : "text-white"}`}>
          {n.label}
        </NavLink>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-x-6 gap-y-8 mt-10 pt-8 border-t border-white/10">
      {MORE_GROUPS.map((g) => (
        <div key={g.key}>
          <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-bold mb-3">{g.title}</p>
          <ul className="space-y-2">
            {g.links.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} onClick={onClose} data-testid={`mobile-nav-${l.key}`}
                  className={({ isActive }) => `text-base ${isActive ? "text-primary" : "text-zinc-300"}`}>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </nav>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/85 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <img src="/brand/logo.png" alt="LEGROSTASDEZIK" className="h-10 md:h-12 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-10">
          {MAIN_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.key}`}>
              {n.label}
            </NavLink>
          ))}
          <MoreMenu />
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/soutenir"
            data-testid="header-support-btn"
            className="inline-flex border border-white/40 text-white font-bold uppercase text-xs tracking-[0.2em] px-5 py-3 hover:border-primary hover:text-primary transition-colors duration-200"
          >
            Soutenir
          </Link>
          <a
            href="#newsletter"
            data-testid="header-newsletter-btn"
            className="inline-flex bg-primary text-white font-bold uppercase text-xs tracking-[0.2em] px-5 py-3 hover:bg-white hover:text-black transition-colors duration-200"
          >
            Newsletter
          </a>
        </div>
        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};
